import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
    getNotificationSettings,
    updateNotificationSettings,
} from "../../helpers/backend_helper";
import { resolveApiMessage } from "../../helpers/apiMessage";
import { registerWebPushSubscription } from "../../helpers/pushNotifications";

type DisplayMode = "standalone" | "browser";
type Platform = "ios" | "android" | "other";
type NotificationMode = "enabled" | "disabled";

type NotificationSettingData = {
    web_push_enabled: boolean;
    quiet_hours_enabled: boolean;
    quiet_hours_start: string | null;
    quiet_hours_end: string | null;
    timezone: string;
    muted_until: string | null;
};

type ApiResponse<T> = {
    data?: T;
    message?: string;
};

const DEFAULT_TIMEZONE = "Asia/Ho_Chi_Minh";
const DEFAULT_START_TIME = "08:00";
const DEFAULT_END_TIME = "22:00";

const getDisplayMode = (): DisplayMode => {
    if (window.matchMedia && window.matchMedia("(display-mode: standalone)").matches) {
        return "standalone";
    }

    if ((window.navigator as { standalone?: boolean }).standalone === true) {
        return "standalone";
    }

    return "browser";
};

const getPlatform = (): Platform => {
    const userAgent = window.navigator.userAgent || "";
    const platform = window.navigator.platform || "";
    const maxTouchPoints = window.navigator.maxTouchPoints || 0;

    const isIOS =
        /iPad|iPhone|iPod/.test(userAgent) ||
        (platform === "MacIntel" && maxTouchPoints > 1);

    if (isIOS) {
        return "ios";
    }

    if (/Android/i.test(userAgent)) {
        return "android";
    }

    return "other";
};

const getBrowserTimezone = () => {
    try {
        return Intl.DateTimeFormat().resolvedOptions().timeZone || DEFAULT_TIMEZONE;
    } catch (error) {
        return DEFAULT_TIMEZONE;
    }
};

const normalizeTime = (value?: string | null) => (value ? value.slice(0, 5) : "");

const resolveMode = (setting: NotificationSettingData): NotificationMode =>
    setting.web_push_enabled ? "enabled" : "disabled";

const isPushSupported = () =>
    "Notification" in window && "serviceWorker" in navigator && "PushManager" in window;

const permissionLabel = (permission: NotificationPermission | string) => {
    switch (permission) {
        case "granted":
            return "Đã bật";
        case "denied":
            return "Đã tắt";
        case "default":
            return "Chưa quyết định";
        default:
            return "Không hỗ trợ";
    }
};

const NotificationsSettings = () => {
    const [supported, setSupported] = useState(false);
    const [permission, setPermission] = useState<NotificationPermission | "unsupported">("unsupported");
    const [displayMode, setDisplayMode] = useState<DisplayMode>("browser");
    const [platform, setPlatform] = useState<Platform>("other");
    const [loading, setLoading] = useState(false);
    const [saving, setSaving] = useState(false);
    const [fetchingSettings, setFetchingSettings] = useState(true);
    const [statusMessage, setStatusMessage] = useState<string | null>(null);
    const [settingsMessage, setSettingsMessage] = useState<string | null>(null);
    const [settingsError, setSettingsError] = useState<string | null>(null);
    const [mode, setMode] = useState<NotificationMode>("enabled");
    const [quietHoursEnabled, setQuietHoursEnabled] = useState(false);
    const [quietHoursStart, setQuietHoursStart] = useState(DEFAULT_START_TIME);
    const [quietHoursEnd, setQuietHoursEnd] = useState(DEFAULT_END_TIME);
    const [timezone, setTimezone] = useState(getBrowserTimezone);

    useEffect(() => {
        const supportedStatus = isPushSupported();

        setSupported(supportedStatus);
        setDisplayMode(getDisplayMode());
        setPlatform(getPlatform());

        if (!supportedStatus) {
            setPermission("unsupported");
            return;
        }

        setPermission(Notification.permission);
    }, []);

    useEffect(() => {
        let mounted = true;

        const loadSettings = async () => {
            setFetchingSettings(true);
            setSettingsError(null);

            try {
                const response = await getNotificationSettings() as ApiResponse<NotificationSettingData>;
                const setting = response.data;

                if (!mounted || !setting) {
                    return;
                }

                setMode(resolveMode(setting));
                setQuietHoursEnabled(setting.quiet_hours_enabled);
                setQuietHoursStart(normalizeTime(setting.quiet_hours_start) || DEFAULT_START_TIME);
                setQuietHoursEnd(normalizeTime(setting.quiet_hours_end) || DEFAULT_END_TIME);
                setTimezone(setting.timezone || getBrowserTimezone());
            } catch (error) {
                if (mounted) {
                    setSettingsError(resolveApiMessage(error, { fallbackMessage: "Không thể tải cài đặt thông báo." }));
                }
            } finally {
                if (mounted) {
                    setFetchingSettings(false);
                }
            }
        };

        loadSettings();

        return () => {
            mounted = false;
        };
    }, []);

    const environmentHint = useMemo(() => {
        if (displayMode === "standalone") {
            return "Ứng dụng đang chạy ở chế độ màn hình chính.";
        }

        if (platform === "ios") {
            return "Trên iPhone, hãy mở ứng dụng từ Màn hình chính để nhận thông báo.";
        }

        return "Bạn đang mở ứng dụng trong trình duyệt.";
    }, [displayMode, platform]);

    const deniedInstruction = useMemo(() => {
        if (platform === "ios") {
            if (displayMode === "standalone") {
                return "Vào Cài đặt iPhone > Thông báo > chọn ứng dụng này > bật Cho phép thông báo.";
            }

            return "Trên iPhone, hãy thêm ứng dụng vào Màn hình chính trước, sau đó mở ứng dụng từ Màn hình chính và bật thông báo.";
        }

        if (platform === "android") {
            return "Mở Chrome > vào trang này > bấm biểu tượng ổ khóa hoặc thông tin trang > Quyền > Thông báo > Cho phép. Hoặc vào Chrome > Cài đặt > Cài đặt trang web > Thông báo.";
        }

        return "Vào cài đặt quyền thông báo của trình duyệt cho trang web này và chuyển sang Cho phép.";
    }, [platform, displayMode]);

    const handleRequestPermission = useCallback(async () => {
        setLoading(true);
        setStatusMessage(null);

        try {
            const result = await registerWebPushSubscription({ requestPermission: true });

            if (result === "unsupported") {
                setPermission("unsupported");
                setStatusMessage("Thiết bị hoặc trình duyệt chưa hỗ trợ thông báo.");
            } else if (result === "missing-vapid") {
                setStatusMessage("Thiếu cấu hình VAPID cho web push. Vui lòng kiểm tra biến môi trường.");
            } else if (result === "granted") {
                setPermission("granted");
                setStatusMessage("Đã đăng ký thông báo thành công.");
            } else if (result === "denied") {
                setPermission("denied");
                setStatusMessage("Bạn đã tắt thông báo. Hãy bật lại quyền thông báo theo hướng dẫn bên dưới.");
            } else if (result === "default") {
                setPermission("default");
                setStatusMessage("Trình duyệt chưa hiển thị hộp thoại. Hãy thử lại.");
            } else {
                setStatusMessage("Không xác định được trạng thái đăng ký thông báo.");
            }
        } catch (error) {
            console.error("Failed to register web push subscription:", error);

            if ("Notification" in window) {
                setPermission(Notification.permission);
            }

            setStatusMessage("Không thể đăng ký thông báo. Vui lòng thử lại.");
        } finally {
            setLoading(false);
        }
    }, []);

    const buildPayload = () => {
        const shouldUseQuietHours = mode === "enabled" && quietHoursEnabled;

        return {
            web_push_enabled: mode === "enabled",
            quiet_hours_enabled: shouldUseQuietHours,
            quiet_hours_start: shouldUseQuietHours ? quietHoursStart : null,
            quiet_hours_end: shouldUseQuietHours ? quietHoursEnd : null,
            timezone: timezone || DEFAULT_TIMEZONE,
            muted_until: null,
        };
    };

    const handleSave = async () => {
        setSettingsMessage(null);
        setSettingsError(null);

        if (mode === "enabled" && quietHoursEnabled && quietHoursEnd <= quietHoursStart) {
            setSettingsError("Giờ đến phải lớn hơn giờ từ.");
            return;
        }

        setSaving(true);

        try {
            const response = await updateNotificationSettings(buildPayload()) as ApiResponse<NotificationSettingData>;
            const setting = response.data;

            if (setting) {
                setMode(resolveMode(setting));
                setQuietHoursEnabled(setting.quiet_hours_enabled);
                setQuietHoursStart(normalizeTime(setting.quiet_hours_start) || quietHoursStart);
                setQuietHoursEnd(normalizeTime(setting.quiet_hours_end) || quietHoursEnd);
                setTimezone(setting.timezone || timezone);
            }

            setSettingsMessage(response.message || "Đã lưu cài đặt thông báo.");
        } catch (error) {
            setSettingsError(resolveApiMessage(error, { fallbackMessage: "Không thể lưu cài đặt thông báo." }));
        } finally {
            setSaving(false);
        }
    };

    return (
        <div className="container py-3">
            <div className="card">
                <div className="card-body">
                    <h5 className="card-title mb-3">Cài đặt thông báo</h5>

                    {fetchingSettings ? (
                        <div className="text-muted">Đang tải cài đặt thông báo...</div>
                    ) : (
                        <>
                            {settingsError && (
                                <div className="alert alert-danger">{settingsError}</div>
                            )}

                            {settingsMessage && (
                                <div className="alert alert-success">{settingsMessage}</div>
                            )}

                            <div className="mb-4">
                                <label className="form-label fw-semibold">Trạng thái nhận thông báo</label>

                                <div className="d-flex flex-column gap-2">
                                    <div className="form-check">
                                        <input
                                            className="form-check-input"
                                            type="radio"
                                            name="notification_mode"
                                            id="notification_mode_enabled"
                                            checked={mode === "enabled"}
                                            onChange={() => setMode("enabled")}
                                        />
                                        <label className="form-check-label" htmlFor="notification_mode_enabled">
                                            Bật
                                        </label>
                                    </div>

                                    <div className="form-check">
                                        <input
                                            className="form-check-input"
                                            type="radio"
                                            name="notification_mode"
                                            id="notification_mode_disabled"
                                            checked={mode === "disabled"}
                                            onChange={() => setMode("disabled")}
                                        />
                                        <label className="form-check-label" htmlFor="notification_mode_disabled">
                                            Tắt
                                        </label>
                                    </div>
                                </div>
                            </div>

                            {mode === "enabled" && (
                                !supported ? (
                                    <div className="alert alert-warning mb-0">
                                        Trình duyệt hiện tại chưa hỗ trợ thông báo đẩy.
                                    </div>
                                ) : (
                                    <>
                                        <div className="mb-3">
                                            <div className="text-muted small">Trạng thái hiện tại</div>
                                            <div className="fw-semibold">{permissionLabel(permission)}</div>
                                        </div>

                                        <div className="mb-3">
                                            <div className="text-muted small">Môi trường</div>
                                            <div>{environmentHint}</div>
                                        </div>

                                        {statusMessage && (
                                            <div className="alert alert-info">{statusMessage}</div>
                                        )}

                                        {permission === "denied" && (
                                            <div className="alert alert-warning">
                                                <div className="fw-semibold mb-1">
                                                    Thông báo đang bị chặn
                                                </div>

                                                <div className="mb-2">
                                                    Vì trình duyệt đã bị chặn quyền thông báo, ứng dụng không thể tự mở lại hộp thoại xin quyền.
                                                </div>

                                                <div>
                                                    {deniedInstruction}
                                                </div>
                                            </div>
                                        )}

                                        <button
                                            type="button"
                                            className="btn btn-primary"
                                            onClick={handleRequestPermission}
                                            disabled={loading}
                                        >
                                            {loading
                                                ? "Đang xử lý..."
                                                : permission === "granted"
                                                    ? "Đăng ký lại thông báo"
                                                    : "Bật thông báo"}
                                        </button>
                                    </>
                                )
                            )}

                            {mode === "enabled" && (
                                <div className="mt-4">
                                    <div className="form-check mb-3">
                                        <input
                                            className="form-check-input"
                                            type="checkbox"
                                            id="quiet_hours_enabled"
                                            checked={quietHoursEnabled}
                                            onChange={(event) => setQuietHoursEnabled(event.target.checked)}
                                        />
                                        <label className="form-check-label" htmlFor="quiet_hours_enabled">
                                            Chọn khung giờ báo
                                        </label>
                                    </div>

                                    {quietHoursEnabled && (
                                        <div className="row g-3 mb-4">
                                            <div className="col-12 col-md-6">
                                                <label className="form-label" htmlFor="quiet_hours_start">
                                                    Từ giờ
                                                </label>
                                                <input
                                                    id="quiet_hours_start"
                                                    type="time"
                                                    className="form-control"
                                                    value={quietHoursStart}
                                                    onChange={(event) => setQuietHoursStart(event.target.value)}
                                                />
                                            </div>

                                            <div className="col-12 col-md-6">
                                                <label className="form-label" htmlFor="quiet_hours_end">
                                                    Đến giờ
                                                </label>
                                                <input
                                                    id="quiet_hours_end"
                                                    type="time"
                                                    className="form-control"
                                                    value={quietHoursEnd}
                                                    min={quietHoursStart}
                                                    onChange={(event) => setQuietHoursEnd(event.target.value)}
                                                />
                                            </div>
                                        </div>
                                    )}
                                </div>
                            )}

                            <div className="mt-4">
                                <button
                                    type="button"
                                    className="btn btn-success"
                                    onClick={handleSave}
                                    disabled={saving || fetchingSettings}
                                >
                                    {saving ? "Đang lưu..." : "Lưu"}
                                </button>
                            </div>
                        </>
                    )}
                </div>
            </div>
        </div>
    );
};

export default NotificationsSettings;
